import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { ChevronLeft, ChevronRight } from 'assets/icons';
import ApiDatabase from 'server';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const MONTH_LABELS = [
  'Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin',
  'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.',
];

const StatsContent = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('xsrfToken');
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [isWaiting, setIsWaiting] = useState(true);

  const year = String(selectedYear.getFullYear());

  const goToMonth = (monthStr) => {
    navigate(`/payslipManagement/payslips?y=${year}&m=${monthStr}`);
  };

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.payslipStats(
      { token, year },
      (data) => {
        setStats(data);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
        setIsWaiting(false);
      }
    );
  }, [year]);

  const chartData = stats?.months?.map((m, i) => ({
    name: MONTH_LABELS[i],
    month: m.month,
    montant: m.totalAmount,
    fiches: m.count,
  })) || [];

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 text-sm">
          <button
            className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
            onClick={() =>
              setSelectedYear(
                (prev) => new Date(prev.getFullYear() - 1, 0, 1)
              )
            }
          >
            <ChevronLeft wh={16} color="#374151" />
          </button>
          <DatePicker
            selected={selectedYear}
            onChange={(date) => setSelectedYear(date)}
            showYearPicker
            dateFormat="yyyy"
            className="w-full min-w-[80px] h-10 ft-b border rounded-lg text-center text-sm max-h-[38px]"
            locale="fr-FR"
          />
          <button
            className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
            onClick={() =>
              setSelectedYear(
                (prev) => new Date(prev.getFullYear() + 1, 0, 1)
              )
            }
          >
            <ChevronRight wh={16} color="#374151" />
          </button>
        </div>

        {!isWaiting && stats && (
          <div className="flex gap-4">
            <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50">
              Total année : {stats.yearTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50">
              Fiches : {stats.yearCount}
            </div>
          </div>
        )}
      </div>

      {isWaiting ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="h-64 w-full bg-gray-100 rounded animate-pulse" />
        </div>
      ) : (
        <div className="flex-1 min-h-0" style={{ minHeight: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'montant') return [`${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`, 'Montant'];
                  return [value, 'Fiches'];
                }}
                labelFormatter={(label) => label}
              />
              <Bar
                dataKey="montant"
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
                cursor="pointer"
                onClick={(data) => {
                  if (data?.month) goToMonth(data.month);
                }}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.montant > 0 ? '#F59E0B' : '#E5E7EB'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!isWaiting && stats && (
        <div className="overflow-auto flex-shrink-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-3 py-2">Mois</th>
                <th className="px-3 py-2 text-right">Fiches</th>
                <th className="px-3 py-2 text-right">Montant</th>
              </tr>
            </thead>
            <tbody>
              {stats.months.map((m, i) => (
                <tr
                  key={m.month}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => goToMonth(m.month)}
                >
                  <td className="px-3 py-2">{MONTH_LABELS[i]}</td>
                  <td className="px-3 py-2 text-right">{m.count}</td>
                  <td className="px-3 py-2 text-right">{m.totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{stats.yearCount}</td>
                <td className="px-3 py-2 text-right">{stats.yearTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StatsContent;
